/**
 * Global offline indicator. Shows when the device is offline, along with how
 * stale the cached data is ("showing data from X ago"). Freshness comes from the
 * max `dataUpdatedAt` across the persisted (allowlisted) queries.
 *
 * Mirrors the softphone call bar: fixed to the top, padded below the device
 * status bar (safe-area inset), and reserves layout space via --offline-bar-offset
 * so screens push their content down instead of being overlapped.
 */
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { shouldDehydrateQuery } from '@/lib/queryPersister'

dayjs.extend(relativeTime)

const OFFLINE_BAR_HEIGHT = 28 // content height (excludes the safe-area inset above it)

function useLastSyncedAt(): number | null {
  const queryClient = useQueryClient()
  const [lastSynced, setLastSynced] = useState<number | null>(null)

  useEffect(() => {
    const cache = queryClient.getQueryCache()
    const recompute = () => {
      let max = 0
      for (const query of cache.getAll()) {
        if (shouldDehydrateQuery(query) && query.state.dataUpdatedAt > max) {
          max = query.state.dataUpdatedAt
        }
      }
      setLastSynced(max || null)
    }
    recompute()
    return cache.subscribe(recompute)
  }, [queryClient])

  return lastSynced
}

export function OfflineBanner() {
  const isOnline = useOnlineStatus()
  const lastSynced = useLastSyncedAt()
  // Re-render every 30s so the relative time stays current while offline.
  const [, setTick] = useState(0)

  useEffect(() => {
    if (isOnline) return
    const id = setInterval(() => setTick((t) => t + 1), 30_000)
    return () => clearInterval(id)
  }, [isOnline])

  // Reserve top space on every screen (.screen-container / .safe-area-top add
  // --offline-bar-offset to their top padding) so the bar pushes content down.
  useEffect(() => {
    if (isOnline) return
    document.documentElement.style.setProperty('--offline-bar-offset', `${OFFLINE_BAR_HEIGHT}px`)
    return () => document.documentElement.style.setProperty('--offline-bar-offset', '0px')
  }, [isOnline])

  if (isOnline) return null

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        insetInline: 0,
        top: 0,
        zIndex: 2000,
        background: '#b45309', // amber-700
        color: '#fff',
        // Drop the content below the device status bar / notch.
        paddingTop: 'max(env(safe-area-inset-top, 0px), var(--android-safe-area-top, 0px))',
      }}
    >
      <div
        style={{
          height: OFFLINE_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 12px',
          fontSize: 13,
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        Offline{lastSynced ? ` — showing data from ${dayjs(lastSynced).fromNow()}` : ' — no cached data available'}
      </div>
    </div>
  )
}
